import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    fetchBrands, createOrder, staffLogin,
    loyaltyLookup,
} from "@/lib/api";
import { formatMoney } from "@/lib/orderUtils";
import { useMemberDiscountRule, computeMemberDiscount, memberDiscountLabel } from "@/lib/memberDiscount";
import { ModifierPicker } from "@/lib/ModifierPicker";
import { groupItemsByCategory } from "@/lib/menuGrouping";
import CategoryNav from "@/components/CategoryNav";
import { optimizedImage, optimizedSrcSet } from "@/lib/cloudinaryImage";

// ===== Inline PIN gate (reuses Hub staff PIN) =====
const CounterGate = ({ onUnlocked }) => {
    const [pin, setPin] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const tap = (d) => pin.length < 6 && setPin((p) => p + d);
    const back = () => setPin((p) => p.slice(0, -1));

    const submit = async (e) => {
        e?.preventDefault();
        setBusy(true); setErr("");
        try {
            const r = await staffLogin(pin);
            localStorage.setItem("naami_staff", JSON.stringify({ name: r.name, token: r.token, role: r.role }));
            onUnlocked(r);
        } catch {
            setErr("Invalid PIN");
            setPin("");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex items-center justify-center px-6" data-testid="counter-gate">
            <Link to="/" className="absolute top-6 left-6 font-mono text-[10px] uppercase tracking-[0.22em] text-muted hover:text-gold">
                ← naami<span className="text-gold">.</span> hub
            </Link>
            <div className="w-full max-w-md">
                <p className="eyebrow text-center justify-center mb-3">// Counter Staff</p>
                <h1 className="font-display text-[40px] text-center mb-2 leading-tight">
                    Enter your <span className="text-gold">PIN.</span>
                </h1>
                <p className="text-muted text-center text-sm mb-10">
                    Demo PIN: <span className="font-mono text-gold">4321</span>
                </p>
                <form onSubmit={submit}>
                    <div className="flex justify-center gap-3 mb-8">
                        {[0,1,2,3,4,5].map((i) => (
                            <div key={i} className={`w-12 h-14 border ${i<pin.length ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--border)]"} grid place-items-center font-mono text-2xl text-gold`}>
                                {i<pin.length ? "●" : ""}
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-6">
                        {[1,2,3,4,5,6,7,8,9].map((d) => (
                            <button key={d} type="button" onClick={() => tap(String(d))} className="h-16 border border-[var(--border)] hover:border-[var(--gold)] font-display text-2xl transition-colors" data-testid={`counter-pin-${d}`}>{d}</button>
                        ))}
                        <button type="button" onClick={back} className="h-16 border border-[var(--border)] hover:border-[var(--gold)] font-mono text-[10px] uppercase tracking-[0.22em]">Del</button>
                        <button type="button" onClick={() => tap("0")} className="h-16 border border-[var(--border)] hover:border-[var(--gold)] font-display text-2xl">0</button>
                        <button type="submit" disabled={busy || pin.length<4} className="h-16 bg-[var(--gold)] text-[var(--bg)] disabled:opacity-30 font-mono uppercase tracking-[0.18em] font-bold" data-testid="counter-pin-submit">Enter</button>
                    </div>
                    {err && <p className="text-[#ff5246] text-sm text-center font-mono">{err}</p>}
                </form>
            </div>
        </div>
    );
};

// ===== Counter (POS-style staff order entry) =====
const Counter = () => {
    const [staff, setStaff] = useState(() => {
        try { return JSON.parse(localStorage.getItem("naami_staff") || "null"); }
        catch { return null; }
    });
    const navigate = useNavigate();
    const [brands, setBrands] = useState([]);
    const [activeBrandId, setActiveBrandId] = useState(null);
    const [search, setSearch] = useState("");
    const [lines, setLines] = useState([]);
    const [customerName, setCustomerName] = useState("");
    const [notes, setNotes] = useState("");
    const [payMethod, setPayMethod] = useState("card");
    const [member, setMember] = useState(null);
    const [phoneLookup, setPhoneLookup] = useState("");
    const [redeemPoints, setRedeemPoints] = useState(0);
    const [placing, setPlacing] = useState(false);
    const [flash, setFlash] = useState(null); // { number, total }
    const [recent, setRecent] = useState([]);
    const [pickerItem, setPickerItem] = useState(null);
    const flashTimer = useRef(null);

    useEffect(() => {
        if (!staff) return;
        fetchBrands().then((b) => setBrands(b));
    }, [staff]);

    const activeBrand = useMemo(
        () => brands.find((b) => b.id === activeBrandId),
        [brands, activeBrandId]
    );

    const filteredItems = useMemo(() => {
        if (!activeBrand) return [];
        const q = search.trim().toLowerCase();
        if (!q) return activeBrand.items || [];
        return (activeBrand.items || []).filter((i) =>
            i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q)
        );
    }, [activeBrand, search]);

    const subtotal = useMemo(
        () => Math.round(lines.reduce((s, l) => s + l.qty * l.price, 0) * 100) / 100,
        [lines]
    );
    const discount = Math.min((redeemPoints || 0) * 0.05, subtotal);
    const memberRule = useMemberDiscountRule();
    const memberDiscount = computeMemberDiscount(memberRule, { member: member?.id ? member : null, hasCode: false, subtotal });
    const total = Math.max(0, Math.round((subtotal - Math.min(discount + memberDiscount, subtotal)) * 100) / 100);

    const addItem = (item, mods = [], priceOverride = null) => {
        if (!activeBrand) return;
        // If item has modifier groups and no mods were chosen yet, open picker
        if ((item.modifier_groups?.length || 0) > 0 && mods.length === 0 && priceOverride === null) {
            setPickerItem(item);
            return;
        }
        const modDelta = mods.reduce((s, m) => s + (m.price_delta || 0), 0);
        const unitPrice = priceOverride !== null ? priceOverride : Math.round((item.price + modDelta) * 100) / 100;
        // Each modifier combination = new line (so two variants don't merge)
        const sig = mods.map(m => `${m.group_name}:${m.option_name}`).sort().join("|");
        setLines((cur) => {
            const idx = cur.findIndex((l) => l.item_id === item.id && (l._modSig || "") === sig);
            if (idx >= 0) {
                const n = [...cur];
                n[idx] = { ...n[idx], qty: n[idx].qty + 1 };
                return n;
            }
            return [...cur, {
                item_id: item.id, brand_id: activeBrand.id, brand_name: activeBrand.name,
                brand_token: activeBrand.token, brand_accent: activeBrand.accent,
                name: item.name, price: unitPrice, base_price: item.price, qty: 1,
                modifiers: mods, _modSig: sig,
            }];
        });
    };

    const changeQty = (id, delta, modSig = null) => {
        setLines((cur) => cur.map((l) =>
            l.item_id === id && (l._modSig || "") === (modSig || "") ? { ...l, qty: l.qty + delta } : l
        ).filter((l) => l.qty > 0));
    };
    const removeLine = (id, modSig = null) =>
        setLines((cur) => cur.filter((l) => !(l.item_id === id && (l._modSig || "") === (modSig || ""))));

    const resetOrder = () => {
        setLines([]); setCustomerName(""); setNotes("");
        setMember(null); setPhoneLookup(""); setRedeemPoints(0);
        setPayMethod("card");
    };

    const lookupMember = async () => {
        if (!phoneLookup.trim()) return;
        try {
            const m = await loyaltyLookup(phoneLookup, null);
            setMember(m);
        } catch {
            setMember({ notFound: true });
        }
    };

    const place = async () => {
        if (lines.length === 0) return;
        setPlacing(true);
        try {
            const order = await createOrder({
                source: "counter", lines,
                customer_name: customerName || null,
                notes: notes || null,
                pay_method: payMethod,
                loyalty_member_id: member?.id || null,
                redeem_points: redeemPoints || 0,
            });
            setRecent((r) => [{ id: order.id, number: order.order_number, total: order.total, name: customerName }, ...r].slice(0, 8));
            setFlash({ number: order.order_number, total: order.total, id: order.id });
            resetOrder();
            if (flashTimer.current) clearTimeout(flashTimer.current);
            flashTimer.current = setTimeout(() => setFlash(null), 3500);
        } catch (e) {
            alert(e.response?.data?.detail || "Failed to place order");
        } finally { setPlacing(false); }
    };

    const printSlip = (orderId) => window.open(`/print/${orderId}`, "_blank", "noopener");
    const logout = () => {
        localStorage.removeItem("naami_staff");
        setStaff(null);
    };

    if (!staff) return <CounterGate onUnlocked={setStaff} />;

    return (
        <div className="h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col overflow-hidden" data-testid="counter-page">
            {/* Top bar */}
            <header className="border-b border-[var(--border)] shrink-0">
                <div className="flex items-center justify-between gap-3 px-5 py-3">
                    <div className="flex items-center gap-3">
                        <Link to="/" className="font-display text-xl tracking-tight">naami<span className="text-gold">.</span></Link>
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// Counter POS</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <input
                            className="input h-10 text-sm w-72"
                            placeholder="Search dishes…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="counter-search"
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted hidden md:block">
                            {staff?.name || "Counter"}
                        </span>
                        <button onClick={logout} className="btn-ghost h-10" data-testid="counter-logout">
                            Sign out
                        </button>
                    </div>
                </div>
                {/* Brand tabs — quick switch when already inside a brand */}
                {activeBrand && (
                    <div className="border-t border-[var(--border)] overflow-x-auto">
                        <div className="flex gap-1 px-3 py-2 min-w-max" data-testid="counter-brand-tabs">
                            {brands.map((b) => (
                                <button
                                    key={b.id}
                                    onClick={() => { setActiveBrandId(b.id); setSearch(""); }}
                                    className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] border whitespace-nowrap transition-colors ${
                                        activeBrandId === b.id
                                            ? "bg-[var(--gold)] text-[var(--bg)] border-[var(--gold)]"
                                            : "border-[var(--border)] hover:border-[var(--gold)]"
                                    }`}
                                    style={activeBrandId === b.id ? {} : { color: b.accent }}
                                    data-testid={`counter-tab-${b.slug}`}
                                >
                                    {b.token}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </header>

            {/* Body: left items, right basket */}
            <div className="flex-1 flex min-h-0">
                {/* LEFT — brand picker OR items */}
                <div className="flex-1 overflow-y-auto p-4" data-testid="counter-left">
                    {!activeBrand ? (
                        // BRAND PICKER GRID
                        brands.length === 0 ? (
                            <p className="text-muted">Loading brands…</p>
                        ) : (
                            <>
                                <div className="flex items-end justify-between mb-4">
                                    <p className="eyebrow">// Pick a brand</p>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">
                                        {brands.length} brands
                                    </span>
                                </div>
                                <div
                                    className="grid gap-3"
                                    style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
                                    data-testid="counter-brand-grid"
                                >
                                    {brands.map((b) => {
                                        const inCart = lines.filter((l) => l.brand_id === b.id).reduce((s, l) => s + l.qty, 0);
                                        return (
                                            <button
                                                key={b.id}
                                                onClick={() => { setActiveBrandId(b.id); setSearch(""); }}
                                                className="brand-card cursor-pointer text-left"
                                                style={{ "--brand-color": b.accent }}
                                                data-testid={`counter-brand-${b.slug}`}
                                            >
                                                <div className="photo">
                                                    {b.image_url && (
                                                        <img src={optimizedImage(b.image_url, { w: 200 })} srcSet={optimizedSrcSet(b.image_url, 200)} alt={b.name} loading="lazy" />
                                                    )}
                                                    {b.halal && <span className="halal-badge">Halal</span>}
                                                    {inCart > 0 && (
                                                        <span
                                                            className="absolute top-3 right-3 z-[2] font-mono font-bold text-[10px] tracking-[0.18em] uppercase px-2 py-1"
                                                            style={{ background: b.accent, color: "#0a0a0a" }}
                                                        >
                                                            {inCart} in cart
                                                        </span>
                                                    )}
                                                    <div className="photo-overlay" aria-hidden />
                                                    <div className="stripe" aria-hidden />
                                                </div>
                                                <div className="p-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="logo-box" style={{ width: 36, height: 36, fontSize: 14 }}>{b.token}</div>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-display text-[15px] leading-tight truncate" style={{ color: b.accent }}>
                                                                {b.name}
                                                            </div>
                                                            <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted truncate">
                                                                {b.cuisine}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )
                    ) : (
                        // ITEMS FOR ACTIVE BRAND
                        <>
                            <div className="flex items-center justify-between mb-3 sticky top-0 bg-[var(--bg)] py-2 -mt-2 z-10">
                                <button
                                    onClick={() => { setActiveBrandId(null); setSearch(""); }}
                                    className="btn-ghost h-10"
                                    data-testid="counter-back-to-brands"
                                >
                                    ← All brands
                                </button>
                                <div className="flex items-center gap-3">
                                    <div className="logo-box" style={{ width: 32, height: 32, fontSize: 13 }}>{activeBrand.token}</div>
                                    <span className="font-display text-[18px]" style={{ color: activeBrand.accent }}>
                                        {activeBrand.name}
                                    </span>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted hidden md:inline">
                                        · {filteredItems.length} items
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-5" data-testid="counter-items">
                                {(() => {
                                    const grouped = groupItemsByCategory(filteredItems, activeBrand.category_order || [], activeBrand.popular_item_ids || [], activeBrand.category_descriptions || {});
                                    const slug = (s) => `pos-cat-${(s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
                                    const navCats = grouped.map(({ category }) => ({ id: slug(category), label: category }));
                                    return (
                                        <>
                                            <CategoryNav categories={navCats} stickyOffsetPx={0} scrollOffsetPx={70} />
                                            {grouped.map(({ category, items: catItems, isPopular, description }) => (
                                    <div key={category} id={slug(category)} className="scroll-mt-24">
                                        <div className="mb-2 pb-1 border-b border-[var(--border)]">
                                            <div className="flex items-baseline justify-between">
                                                <span className="font-display text-[14px] flex items-baseline gap-1" style={{ color: isPopular ? "var(--gold)" : activeBrand.accent }}>
                                                    {isPopular && <span className="text-[11px]">★</span>}
                                                    {category}
                                                </span>
                                                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted">{isPopular ? "7d" : catItems.length}</span>
                                            </div>
                                            {description && (
                                                <p className="text-[11px] text-[var(--text)]/55 font-light leading-snug mt-0.5">{description}</p>
                                            )}
                                        </div>
                                        <div
                                            className="grid gap-3"
                                            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
                                        >
                                            {catItems.map((it) => {
                                                const qty = lines.filter((l) => l.item_id === it.id).reduce((s, l) => s + l.qty, 0);
                                                return (
                                                    <button
                                                        key={it.id}
                                                        onClick={() => addItem(it)}
                                                        disabled={it.available === false}
                                                        className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] active:scale-[0.99] text-left flex flex-col overflow-hidden transition-all disabled:opacity-40"
                                                        data-testid={`counter-item-${it.id}`}
                                                    >
                                                        {it.image_url && (
                                                            <div className="relative aspect-[4/3] overflow-hidden bg-[var(--surface-elevated)]">
                                                                <img src={optimizedImage(it.image_url, { w: 120 })} srcSet={optimizedSrcSet(it.image_url, 120)} alt={it.name} loading="lazy" className="w-full h-full object-cover" />
                                                                {qty > 0 && (
                                                                    <span className="absolute top-2 right-2 w-9 h-9 rounded-full bg-[var(--gold)] text-[var(--bg)] grid place-items-center font-display text-base">
                                                                        {qty}
                                                                    </span>
                                                                )}
                                                                {it.available === false && (
                                                                    <span className="absolute inset-0 bg-black/60 grid place-items-center font-mono text-[11px] uppercase tracking-[0.22em] text-[#ff5246]">86&apos;d</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="p-3 flex-1 flex flex-col">
                                                            <div className="font-display text-[14px] leading-tight mb-1 line-clamp-2">{it.name}</div>
                                                            <div className="mt-auto flex items-center justify-between">
                                                                <span className="font-mono text-[13px] text-gold">{formatMoney(it.price)}</span>
                                                                {it.popular && <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-gold">★</span>}
                                                            </div>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                                        </>
                                    );
                                })()}
                            </div>
                        </>
                    )}
                </div>

                {/* RIGHT — sticky basket */}
                <aside className="w-[400px] xl:w-[440px] shrink-0 border-l border-[var(--border)] bg-[var(--surface)] flex flex-col" data-testid="counter-basket">
                    <div className="p-4 border-b border-[var(--border)]">
                        <div className="flex items-center justify-between">
                            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">// Current order</span>
                            {lines.length > 0 && (
                                <button onClick={resetOrder} className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff5246] hover:underline" data-testid="counter-clear">
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Lines */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                        {lines.length === 0 ? (
                            <p className="text-muted text-center mt-12 font-mono text-[11px] uppercase tracking-[0.22em]">
                                Tap any dish to add
                            </p>
                        ) : (
                            lines.map((l) => (
                                <div key={`${l.item_id}-${l._modSig || ""}`} className="bg-[var(--surface-elevated)] p-2.5 flex items-center gap-2" data-testid={`counter-line-${l.item_id}`}>
                                    <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted w-8 shrink-0" style={{ color: l.brand_accent }}>
                                        {l.brand_token}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] truncate">{l.name}</div>
                                        {l.modifiers?.length > 0 && (
                                            <div className="font-mono text-[10px] text-gold/80 leading-tight mt-0.5">
                                                {l.modifiers.map(m => m.option_name).join(" · ")}
                                            </div>
                                        )}
                                        <div className="font-mono text-[11px] text-muted">{formatMoney(l.price)}</div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => changeQty(l.item_id, -1, l._modSig)} className="w-8 h-8 border border-[var(--border-strong)] grid place-items-center">−</button>
                                        <span className="font-mono w-7 text-center text-sm">{l.qty}</span>
                                        <button onClick={() => changeQty(l.item_id, 1, l._modSig)} className="w-8 h-8 border border-[var(--border-strong)] grid place-items-center">+</button>
                                    </div>
                                    <button onClick={() => removeLine(l.item_id, l._modSig)} className="text-muted hover:text-[#ff5246] text-lg leading-none w-6 grid place-items-center">
                                        ×
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Customer info + loyalty + payment */}
                    <div className="border-t border-[var(--border)] p-4 space-y-3">
                        <input
                            className="input h-10 text-sm"
                            placeholder="Customer name (optional)"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                            data-testid="counter-name"
                        />
                        <input
                            className="input h-10 text-sm"
                            placeholder="Allergies / notes (optional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            data-testid="counter-notes"
                        />

                        {/* Loyalty quick-lookup */}
                        {!member ? (
                            <div className="flex gap-2">
                                <input
                                    className="input h-10 text-sm flex-1"
                                    placeholder="Naami Club phone (optional)"
                                    value={phoneLookup}
                                    onChange={(e) => setPhoneLookup(e.target.value)}
                                    data-testid="counter-phone-lookup"
                                />
                                <button onClick={lookupMember} disabled={!phoneLookup.trim()} className="btn-ghost h-10" data-testid="counter-lookup-btn">
                                    Look up
                                </button>
                            </div>
                        ) : member.notFound ? (
                            <div className="border border-[var(--border)] p-2 text-[12px] flex items-center justify-between">
                                <span className="text-muted">No member · ask if they want to join</span>
                                <button onClick={() => { setMember(null); setPhoneLookup(""); }} className="text-muted hover:text-gold font-mono text-[10px] uppercase tracking-[0.18em]">Skip</button>
                            </div>
                        ) : (
                            <div className="border border-[var(--gold)]/40 bg-[var(--gold)]/5 p-2.5 text-[12px]" data-testid="counter-member-card">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-gold font-display text-[14px]">✦ {member.name}</span>
                                    <button onClick={() => { setMember(null); setRedeemPoints(0); setPhoneLookup(""); }} className="text-muted hover:text-gold font-mono text-[10px] uppercase tracking-[0.18em]">Remove</button>
                                </div>
                                <div className="font-mono text-[11px] text-muted mb-2">{member.points} pts available</div>
                                {(() => {
                                    const max = Math.min(Math.floor(member.points / 100) * 100, Math.floor(subtotal / 5) * 100);
                                    if (max < 100) return null;
                                    return (
                                        <div className="flex flex-wrap gap-1">
                                            {[0, 100, 200, 300].filter((v) => v <= max).map((v) => (
                                                <button
                                                    key={v}
                                                    onClick={() => setRedeemPoints(v)}
                                                    className={`font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border ${
                                                        redeemPoints === v ? "border-[var(--gold)] bg-[var(--gold)] text-[var(--bg)]" : "border-[var(--border)]"
                                                    }`}
                                                    data-testid={`counter-redeem-${v}`}
                                                >
                                                    {v === 0 ? "None" : `−£${(v/20).toFixed(0)}`}
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* Payment — card only */}
                        <div className="p-3 border border-[var(--gold)] bg-[var(--surface-elevated)]" data-testid="counter-pay-card">
                            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold">Card · Apple Pay · Google Pay</div>
                            <div className="font-mono text-[10px] text-muted mt-0.5">No cash accepted</div>
                        </div>

                        {/* Totals */}
                        <div className="space-y-1 pt-2 border-t border-[var(--border)]">
                            <div className="flex justify-between font-mono text-[11px] text-muted">
                                <span>Subtotal</span><span>{formatMoney(subtotal)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between font-mono text-[11px] text-gold">
                                    <span>−{redeemPoints} pts</span><span>−{formatMoney(discount)}</span>
                                </div>
                            )}
                            {memberDiscount > 0 && (
                                <div className="flex justify-between font-mono text-[11px] text-gold" data-testid="counter-member-discount">
                                    <span>{memberDiscountLabel(memberRule)}</span><span>−{formatMoney(memberDiscount)}</span>
                                </div>
                            )}
                            <div className="flex items-baseline justify-between pt-1">
                                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted">Total</span>
                                <span className="font-display text-[28px] text-gold leading-none" data-testid="counter-total">{formatMoney(total)}</span>
                            </div>
                        </div>

                        <button
                            onClick={place}
                            disabled={placing || lines.length === 0}
                            className="btn-primary w-full justify-center text-base py-4 disabled:opacity-30"
                            data-testid="counter-place-order"
                        >
                            {placing ? "Placing…" : `Take payment · ${formatMoney(total)}`}
                        </button>
                    </div>
                </aside>
            </div>

            {/* Recent orders strip */}
            {recent.length > 0 && (
                <div className="border-t border-[var(--border)] bg-[var(--surface)] px-4 py-2 shrink-0 overflow-x-auto" data-testid="counter-recent">
                    <div className="flex items-center gap-3 min-w-max">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted whitespace-nowrap">// Just sent</span>
                        {recent.map((r) => (
                            <button
                                key={r.id}
                                onClick={() => printSlip(r.id)}
                                className="font-mono text-[11px] uppercase tracking-[0.18em] border border-[var(--border)] hover:border-[var(--gold)] px-2.5 py-1.5 whitespace-nowrap"
                                data-testid={`counter-recent-${r.number}`}
                                title="Click to reprint slip"
                            >
                                #{r.number} · {formatMoney(r.total)}{r.name ? ` · ${r.name}` : ""}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Flash overlay after order */}
            {flash && (
                <div
                    className="fixed inset-0 bg-[var(--bg)]/95 backdrop-blur grid place-items-center z-50 cursor-pointer fade-up"
                    onClick={() => { setFlash(null); if (flashTimer.current) clearTimeout(flashTimer.current); }}
                    data-testid="counter-flash"
                >
                    <div className="text-center max-w-xl px-6">
                        <p className="eyebrow justify-center mb-4">// Sent to kitchen</p>
                        <div className="font-display text-[120px] text-gold leading-none mb-4">#{flash.number}</div>
                        <div className="font-mono text-[20px] text-muted mb-8">{formatMoney(flash.total)} · collect when ready</div>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={(e) => { e.stopPropagation(); printSlip(flash.id); }}
                                className="btn-secondary"
                                data-testid="counter-flash-print"
                            >
                                🖨 Print slips
                            </button>
                            <button
                                onClick={() => { setFlash(null); if (flashTimer.current) clearTimeout(flashTimer.current); }}
                                className="btn-primary"
                                data-testid="counter-flash-next"
                            >
                                Next customer →
                            </button>
                        </div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted mt-8">
                            Auto-clears in 3 seconds
                        </p>
                    </div>
                </div>
            )}

            {pickerItem && (
                <ModifierPicker
                    item={pickerItem}
                    onCancel={() => setPickerItem(null)}
                    onConfirm={(mods, totalPrice) => {
                        addItem(pickerItem, mods, totalPrice);
                        setPickerItem(null);
                    }}
                />
            )}
        </div>
    );
};

export default Counter;
